import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, FileText, Users } from 'lucide-react';

// Security and Privacy Policy
const SECURITY_PRIVACY_POLICY = `Bonbot is committed to protecting the security and privacy of its users.

All personal information collected is used solely for account creation, account identification, and access to Bonbot services. This may include information such as your name, email address, and other details required to create and manage your account.

Bonbot does not sell, share, rent, or disclose personal information to third parties. All user information is handled confidentially and used only for the purposes stated above.

Reasonable security measures are implemented to safeguard personal data against unauthorized access, alteration, disclosure, or misuse. However, users acknowledge that no online system or data transmission method can be guaranteed to be completely secure.

By accessing or using Bonbot, you agree to the collection and use of your personal information strictly in accordance with this Security and Privacy section.`;

// Terms of Service - Account Duplication
const TERMS_OF_SERVICE = `By accessing or using Bonbot, you agree to comply with the following terms regarding account usage and free trials.

Each user is permitted one (1) free trial account only. The creation, use, or attempted use of multiple accounts to obtain additional free trials is strictly prohibited.

Any form of account duplication, including but not limited to registering multiple accounts using different email addresses, devices, IP addresses, identities, or false information, will be considered a violation of these Terms of Service.

Violation of this policy will result in an immediate and permanent ban from the Bonbot platform. Banned users will permanently lose access to all Bonbot services, including current and future offerings. No appeals, refunds, or reinstatements will be granted.

Bonbot reserves the right, at its sole discretion, to monitor, detect, investigate, and enforce action against any user suspected of violating this policy.`;

// No Refund Policy
const NO_REFUND_POLICY = `1. All Sales Are Final
All purchases of Bonbot, including but not limited to software access, subscriptions, and related services, are final and non-refundable. Once access to Bonbot is granted, no refunds, chargebacks, or reversals will be issued under any circumstances.

2. Signal-Only Service (No Automated Trading)
Bonbot is a signal-generating tool only. It does not execute trades, place orders, manage accounts, or trade on behalf of users. All trading decisions, trade execution, position sizing, and risk management remain entirely the responsibility of the user.

3. Trading Risk Disclosure
By purchasing and using Bonbot, you acknowledge and agree that:
• Trading financial markets involves significant risk
• Signals provided by Bonbot are not guaranteed to be accurate or profitable
• Market conditions can change rapidly and without notice
• Past performance does not guarantee future results

Refunds will not be issued due to:
• Trading losses or missed opportunities
• Dissatisfaction with signal accuracy or performance
• Market volatility or unexpected market events
• User execution errors, delays, or misinterpretation of signals

4. No Financial Advice
All signals, alerts, and information provided by Bonbot are for educational and informational purposes only and do not constitute financial, investment, or trading advice. Users should conduct their own research and consult a qualified financial professional if needed before trading.

5. Technical & Service Limitations
While we aim to provide a reliable service, Bonbot is not responsible for:
• Internet or connectivity issues
• Platform, broker, or exchange limitations
• Third-party service outages or failures
• Delayed, missed, or undelivered signals due to factors beyond our control
These circumstances do not qualify for a refund.

6. Chargebacks & Policy Enforcement
Any attempt to initiate a chargeback, payment dispute, or unauthorized reversal will result in immediate termination of access to Bonbot without notice and without refund.

7. Acceptance of Policy
By purchasing, accessing, or using Bonbot, you confirm that you have read, understood, and agreed to this No Refund Policy in full.`;

const PolicySection = ({ title, icon: Icon, content, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-900/50 backdrop-blur-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <Icon className="w-5 h-5 text-purple-400" />
          </div>
          <span className="text-base font-semibold text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <div className="p-4 bg-black/30 rounded-lg max-h-64 overflow-y-auto">
            <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
              {content}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const PolicySections = () => {
  return (
    <div className="w-full max-w-4xl mx-auto mt-12 mb-8 px-4">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-white mb-2">Legal & Policies</h2>
        <p className="text-sm text-gray-400">Please review our policies below</p>
      </div>
      
      <div className="space-y-3">
        <PolicySection
          title="Security and Privacy"
          icon={Shield}
          content={SECURITY_PRIVACY_POLICY}
        />
        
        <PolicySection
          title="Terms of Service – Account Duplication & Free Trial"
          icon={Users}
          content={TERMS_OF_SERVICE}
        />
        
        <PolicySection
          title="No Refund Policy"
          icon={FileText}
          content={NO_REFUND_POLICY}
        />
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          By using Bonbot, you agree to all policies listed above.
        </p>
      </div>
    </div>
  );
};

export default PolicySections;
